import * as React from "react";
import type { Ref } from "react";
import { Link, type LinkProps } from "react-router-dom";

import { faCheck, faChevronRight, faExternalLink } from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import * as ContextMenuPrimitive from "@radix-ui/react-context-menu";
import {
	type ContextMenuCheckboxItemProps,
	type ContextMenuContentProps,
	type ContextMenuItemProps,
	type ContextMenuLabelProps,
	type ContextMenuPortalProps,
	type ContextMenuProps,
	type ContextMenuRadioGroupProps,
	type ContextMenuRadioItemProps,
	type ContextMenuSeparatorProps,
	type ContextMenuSubContentProps,
	type ContextMenuSubProps,
	type ContextMenuSubTriggerProps,
	type ContextMenuTriggerProps,
} from "@radix-ui/react-context-menu";
import cx from "classnames";
import { Emoji } from "emoji-mart";
import { noop } from "lodash";

import { Checkbox } from "~/ui";
import Avatar, { type AvatarProps } from "~/ui/legacy/Avatar";
import { Switch } from "~/ui/legacyFellowIndex";

import styles from "../menu/menu.module.css";

export function ContextMenu({ ...props }: ContextMenuProps) {
	const { children, modal, ...rest } = props;

	return (
		<ContextMenuPrimitive.Root {...rest} modal={modal ?? false}>
			{children}
		</ContextMenuPrimitive.Root>
	);
}
ContextMenu.Root = ContextMenu;

const Trigger = React.forwardRef<HTMLButtonElement, ContextMenuTriggerProps>((props, ref) => {
	const { asChild = true, children, className, ...rest } = props;

	return (
		<ContextMenuPrimitive.Trigger {...rest} asChild={asChild} className={cx(styles.trigger, className)} ref={ref}>
			{children}
		</ContextMenuPrimitive.Trigger>
	);
});

Trigger.displayName = "Trigger";
ContextMenu.Trigger = Trigger;

function Portal(props: ContextMenuPortalProps) {
	return <ContextMenuPrimitive.Portal {...props}>{props.children}</ContextMenuPrimitive.Portal>;
}
ContextMenu.Portal = Portal;

const Content = React.forwardRef(function Content(props: ContextMenuContentProps, forwardedRef: Ref<HTMLDivElement>) {
	const { children, className, ...rest } = props;

	return (
		<ContextMenu.Portal>
			<ContextMenuPrimitive.Content {...rest} className={cx(styles.content, className)} ref={forwardedRef}>
				{children}
			</ContextMenuPrimitive.Content>
		</ContextMenu.Portal>
	);
});
ContextMenu.Content = Content;

type AdditionalItemProps = (
	| { icon?: IconDefinition | React.ReactNode }
	| { user?: AvatarProps["user"] }
	| { team?: AvatarProps["team"] }
	| { emoji?: string | null }
) & {
	destructive?: boolean;

	// Radix relies on 'onSelect' for keyboard accessibility
	// Discourage the use of 'onClick' so we can take advantage of that
	/** @deprecated Use onSelect instead */
	onClick?: ContextMenuItemProps["onClick"];
};

type MenuItemProps = ContextMenuItemProps & AdditionalItemProps;

function Item(props: MenuItemProps) {
	const { className, destructive, ...rest } = props;
	const { disabled } = rest;

	return (
		<ContextMenuPrimitive.Item
			{...rest}
			className={cx(styles.item, className, { [styles.itemDestructive]: destructive, [styles.itemDisabled]: disabled })}
		>
			{generateItem(rest)}
		</ContextMenuPrimitive.Item>
	);
}
ContextMenu.Item = Item;

type TriggerItemProps = ContextMenuSubTriggerProps & AdditionalItemProps;

function TriggerItem(props: TriggerItemProps) {
	const { className, destructive, ...rest } = props;
	const { disabled } = rest;

	const itemContent = (
		<>
			{generateItem(rest)}
			<FontAwesomeIcon fixedWidth icon={faChevronRight} className={styles.itemIconSecondary} />
		</>
	);

	return (
		<ContextMenuPrimitive.SubTrigger
			{...rest}
			className={cx(styles.item, className, { [styles.itemDestructive]: destructive, [styles.itemDisabled]: disabled })}
		>
			{itemContent}
		</ContextMenuPrimitive.SubTrigger>
	);
}
ContextMenu.SubTrigger = TriggerItem;

function MenuSub({ ...props }: ContextMenuSubProps) {
	const { children, ...rest } = props;

	return <ContextMenuPrimitive.Sub {...rest}>{children}</ContextMenuPrimitive.Sub>;
}
ContextMenu.Sub = MenuSub;

const SubContent = React.forwardRef<HTMLDivElement, ContextMenuSubContentProps>(
	function SubContent(props, forwardedRef) {
		const { children, className, sideOffset, ...rest } = props;

		return (
			<ContextMenu.Portal>
				<ContextMenuPrimitive.SubContent
					{...rest}
					className={cx(styles.content, className)}
					sideOffset={sideOffset ?? 1}
					ref={forwardedRef}
				>
					{children}
				</ContextMenuPrimitive.SubContent>
			</ContextMenu.Portal>
		);
	},
);
ContextMenu.SubContent = SubContent;

type ItemLinkProps = { hideExternalIcon?: boolean } & (
	| (LinkProps & AdditionalItemProps)
	| (React.HTMLProps<HTMLAnchorElement> & React.AnchorHTMLAttributes<HTMLAnchorElement> & AdditionalItemProps)
);

/** Since react-router has a "to" prop we took advantage of the fact that an {a}-tag does not have this prop.
 * If we specify the "to" prop this component will behave as a react-router link. Otherwise it will behave as
 * an {a}-tag.
 */
function ItemLink(props: ItemLinkProps) {
	const { className, hideExternalIcon, ...rest } = props;

	const itemContent =
		"to" in rest ? (
			<Link {...rest} className={cx(styles.item, className)}>
				{generateItem(rest)}
			</Link>
		) : (
			<a {...rest} className={cx(styles.item, className)}>
				{generateItem(rest)}
				{!hideExternalIcon && <FontAwesomeIcon fixedWidth icon={faExternalLink} className={styles.itemIconSecondary} />}
			</a>
		);

	return <ContextMenuPrimitive.Item asChild>{itemContent}</ContextMenuPrimitive.Item>;
}
ContextMenu.ItemLink = ItemLink;

const isIconDef = (item: IconDefinition | React.ReactNode): item is IconDefinition => {
	return Boolean(item && typeof item == "object" && "icon" in item);
};

function generateItem(props: ContextMenuItemProps | ContextMenuPrimitive.ContextMenuSubTriggerProps | ItemLinkProps) {
	if ("icon" in props) {
		const { children, icon } = props;

		const iconElement = isIconDef(icon) ? (
			<FontAwesomeIcon fixedWidth icon={icon} className={styles.itemIcon} />
		) : (
			<span className={cx(styles.itemIcon, styles.customIcon)}>{icon}</span>
		);

		return (
			<>
				{icon && iconElement}
				<span className={styles.itemText}>{children}</span>
			</>
		);
	} else if ("user" in props || "team" in props || "emoji" in props) {
		const { children, ...rest } = props;

		let user, team, emoji;

		if ("user" in rest) {
			user = rest.user;
		} else if ("team" in rest) {
			team = rest.team;
		} else if ("emoji" in rest) {
			emoji = rest.emoji;
		}

		return (
			<>
				{user && <Avatar circle className={styles.itemAvatar} user={user} />}
				{team && <Avatar circle className={styles.itemAvatar} team={team} />}
				{emoji && (
					<div className={styles.itemEmoji}>
						<Emoji size={14} emoji={emoji} native></Emoji>
					</div>
				)}
				<span className={styles.itemText}>{children}</span>
			</>
		);
	} else {
		const { children } = props;

		return <span className={styles.itemText}>{children}</span>;
	}
}

type CheckboxItemProps = Omit<ContextMenuCheckboxItemProps, "checked" | "onCheckedChange"> & {
	closeOnSelect?: boolean;
	checked?: boolean;
	onCheckedChange?: (checked: boolean) => void;
};

function CheckboxItem(props: CheckboxItemProps) {
	const { children, className, onSelect, closeOnSelect, checked, onCheckedChange, ...rest } = props;

	const onSelectAndConsiderClosing = React.useCallback(
		(event: Event) => {
			if (!closeOnSelect) event.preventDefault();

			return onSelect?.(event);
		},
		[onSelect, closeOnSelect],
	);

	const itemContent = (
		<>
			<Checkbox checked={checked} className={cx(styles.itemIcon, styles.checkbox)} />
			<span className={styles.itemText}>{children}</span>
		</>
	);

	const onCheckedChangeWithCheckedState = React.useCallback(
		(checked: boolean | "indeterminate") => {
			return onCheckedChange?.(checked === true);
		},
		[onCheckedChange],
	);

	return (
		<ContextMenuPrimitive.CheckboxItem
			{...rest}
			checked={checked}
			onCheckedChange={onCheckedChangeWithCheckedState}
			className={cx(styles.item, className)}
			onSelect={onSelectAndConsiderClosing}
		>
			{itemContent}
		</ContextMenuPrimitive.CheckboxItem>
	);
}
ContextMenu.CheckboxItem = CheckboxItem;

const RadioGroupContext = React.createContext<{
	currentValue: string | undefined;
	changeValue: (value: string | undefined) => void;
}>({
	currentValue: undefined,
	changeValue: noop,
});
RadioGroupContext.displayName = "RadioGroupContext";

function RadioGroup(props: ContextMenuRadioGroupProps) {
	const { children, value, onValueChange, ...rest } = props;

	return (
		<ContextMenuPrimitive.RadioGroup {...rest} value={value} onValueChange={onValueChange}>
			{children}
		</ContextMenuPrimitive.RadioGroup>
	);
}
ContextMenu.RadioGroup = RadioGroup;

function RadioItem(props: ContextMenuRadioItemProps & AdditionalItemProps) {
	const { className, ...rest } = props;
	const { disabled } = rest;

	return (
		<ContextMenuPrimitive.RadioItem
			{...rest}
			className={cx(styles.item, className, { [styles.itemDisabled]: disabled })}
		>
			{generateItem(rest)}
			<ContextMenuPrimitive.ItemIndicator>
				<FontAwesomeIcon fixedWidth icon={faCheck} className={styles.radioButton} />
			</ContextMenuPrimitive.ItemIndicator>
		</ContextMenuPrimitive.RadioItem>
	);
}
ContextMenu.RadioItem = RadioItem;

function SwitchItem(props: MenuItemProps & CheckboxItemProps) {
	const { className, onSelect, closeOnSelect, checked, onCheckedChange, ...rest } = props;

	const onSelectAndConsiderClosing = React.useCallback(
		(event: Event) => {
			if (!closeOnSelect) event.preventDefault();

			return onSelect?.(event);
		},
		[onSelect, closeOnSelect],
	);

	const itemContent = (
		<>
			{generateItem(rest)}
			<Switch checked={checked} size="small" disabled={props.disabled} />
		</>
	);

	const onCheckedChangeWithCheckedState = React.useCallback(
		(checked: boolean | "indeterminate") => {
			return onCheckedChange?.(checked === true);
		},
		[onCheckedChange],
	);

	return (
		<ContextMenuPrimitive.CheckboxItem
			{...rest}
			checked={checked}
			onCheckedChange={onCheckedChangeWithCheckedState}
			className={cx(styles.item, styles.switchItem, className, { [styles.itemDisabled]: rest.disabled })}
			onSelect={onSelectAndConsiderClosing}
		>
			{itemContent}
		</ContextMenuPrimitive.CheckboxItem>
	);
}
ContextMenu.SwitchItem = SwitchItem;

function Label(props: ContextMenuLabelProps) {
	const { children, className, ...rest } = props;

	return (
		<ContextMenuPrimitive.Label {...rest} className={cx(styles.label, className)}>
			{children}
		</ContextMenuPrimitive.Label>
	);
}
ContextMenu.Label = Label;

function Separator(props: ContextMenuSeparatorProps) {
	const { children, className, ...rest } = props;

	return (
		<ContextMenuPrimitive.Separator {...rest} className={cx(styles.separator, className)}>
			{children}
		</ContextMenuPrimitive.Separator>
	);
}
ContextMenu.Separator = Separator;

export default ContextMenu;
